import { ref } from 'vue';
import type { SqlInfo } from '@/common/components/organisms/sqlTextWindow/sqlTextWindow.types';
import { DB_TYPE } from '@/common/define/common.define';
import {
  fetchOracleDocumentText,
  getOracleDocumentTextQueryKey,
  fetchPostgresqlDocumentText,
  getPostgresqlDocumentTextQueryKey,
  fetchMysqlDocumentText,
  getMysqlDocumentTextQueryKey,
  fetchSqlServerDocumentText,
  getSqlServerDocumentTextQueryKey,
  fetchTiberoDocumentText,
  getTiberoDocumentTextQueryKey,
  fetchAltibaseDocumentText,
  getAltibaseDocumentTextQueryKey,
  fetchClickhouseDocumentText,
  getClickhouseDocumentTextQueryKey,
  fetchRedshiftDocumentText,
  getRedshiftDocumentTextQueryKey,
  fetchSinglestoreDocumentText,
  getSinglestoreDocumentTextQueryKey,
  fetchMongodbDocumentText,
  getMongodbDocumentTextQueryKey,
  fetchCubridDocumentText,
  getCubridDocumentTextQueryKey,
} from './db';
import { InvalidDatabaseTypeError } from './error.utils';
import type { DocumentTextParams, FullTextDbType } from './types';

const getDocumentText = (
  data: { sqlSampleText?: string; sqlText?: string } | string | null,
  useSampleText?: boolean,
): string => {
  if (data && typeof data === 'object') {
    if (useSampleText) {
      return data.sqlSampleText || '';
    }
    return data.sqlText || '';
  }

  return data || '';
};
const getDocumentType = (dbType: FullTextDbType) => (dbType === DB_TYPE.MONGODB ? 'json' : 'sql');

export const FULL_TEXT_GETTER_OBJ = {
  [DB_TYPE.MYSQL]: fetchMysqlDocumentText,
  [DB_TYPE.POSTGRESQL]: fetchPostgresqlDocumentText,
  [DB_TYPE.ORACLE]: fetchOracleDocumentText,
  [DB_TYPE.SQLSERVER]: fetchSqlServerDocumentText,
  [DB_TYPE.TIBERO]: fetchTiberoDocumentText,
  [DB_TYPE.ALTIBASE]: fetchAltibaseDocumentText,
  [DB_TYPE.CLICKHOUSE]: fetchClickhouseDocumentText,
  [DB_TYPE.REDSHIFT]: fetchRedshiftDocumentText,
  [DB_TYPE.SINGLESTORE]: fetchSinglestoreDocumentText,
  [DB_TYPE.MONGODB]: fetchMongodbDocumentText,
  [DB_TYPE.CUBRID]: fetchCubridDocumentText,
};

export const FULL_TEXT_QUERY_KEY_GETTER_OBJ = {
  [DB_TYPE.MYSQL]: getMysqlDocumentTextQueryKey,
  [DB_TYPE.POSTGRESQL]: getPostgresqlDocumentTextQueryKey,
  [DB_TYPE.ORACLE]: getOracleDocumentTextQueryKey,
  [DB_TYPE.SQLSERVER]: getSqlServerDocumentTextQueryKey,
  [DB_TYPE.TIBERO]: getTiberoDocumentTextQueryKey,
  [DB_TYPE.ALTIBASE]: getAltibaseDocumentTextQueryKey,
  [DB_TYPE.CLICKHOUSE]: getClickhouseDocumentTextQueryKey,
  [DB_TYPE.REDSHIFT]: getRedshiftDocumentTextQueryKey,
  [DB_TYPE.SINGLESTORE]: getSinglestoreDocumentTextQueryKey,
  [DB_TYPE.MONGODB]: getMongodbDocumentTextQueryKey,
  [DB_TYPE.CUBRID]: getCubridDocumentTextQueryKey,
};

export const useDocumentTextWindow = () => {
  const isDocumentWindowOpen = ref<boolean>(false);
  const isDocumentWindowLoading = ref<boolean>(false);
  const documentInfo = ref<SqlInfo | null>(null);

  const openDocumentWindow = (params: {
    text: string;
    textType?: 'sql' | 'json' | 'none';
    title?: string;
  }) => {
    documentInfo.value = params;
    isDocumentWindowOpen.value = true;
  };

  const openDocumentWindowWithApiCall = async (params: DocumentTextParams, dbType: string = '') => {
    documentInfo.value = null;
    isDocumentWindowOpen.value = true;
    isDocumentWindowLoading.value = true;
    try {
      const fullTextGetterFn = FULL_TEXT_GETTER_OBJ[dbType];
      if (!fullTextGetterFn) {
        throw new InvalidDatabaseTypeError({ dbType });
      }

      const sqlFullText = await fullTextGetterFn({
        ...params,
        useFieldValidation: params.useFieldValidation ?? true,
      });

      documentInfo.value = {
        text: getDocumentText(sqlFullText, params.useSampleText),
        textType: getDocumentType(dbType as FullTextDbType),
        title: params.title,
      };
    } catch (e: unknown) {
      isDocumentWindowOpen.value = false;
      throw e;
    } finally {
      isDocumentWindowLoading.value = false;
    }
  };

  return {
    isDocumentWindowOpen,
    isDocumentWindowLoading,
    documentInfo,

    openDocumentWindow,
    openDocumentWindowWithApiCall,
  };
};
